import React, { useState, useMemo } from 'react';
import {
  Combobox,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
  ComboboxButton,
} from '@headlessui/react';
import { Plus, X, Tag } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useGetProjectLabelsQuery } from 'service/project/hooks';
import { useCreateTaskLabelMutation } from 'service/project/hooks/useCreateTaskLabelMutation';

export interface LabelItem {
  uuid: string;
  name: string;
}

interface LabelPickerProps {
  projectId: string;
  selectedLabelUuids: string[];
  onLabelsChange: (uuids: string[]) => void;
  disabled?: boolean;
  className?: string;
}

const LabelPicker: React.FC<LabelPickerProps> = ({
  projectId,
  selectedLabelUuids,
  onLabelsChange,
  disabled = false,
  className = '',
}) => {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');

  // Fetch project labels directly from the new endpoint
  const { data: projectLabels } = useGetProjectLabelsQuery(projectId);

  // Use the existing mutation hook
  const { mutate: createLabel, isPending: isCreating } = useCreateTaskLabelMutation();

  // Map the new ProjectLabel interface to LabelItem
  const existingLabels: LabelItem[] = useMemo(() => {
    if (!projectLabels) {
      return [];
    }
    return projectLabels.filter(lbl => lbl && lbl.projectLabelName).map((lbl) => ({
      uuid: lbl.projectLabelUuid,
      name: lbl.projectLabelName,
    }));
  }, [projectLabels]);

  // Filter existing labels based on query AND exclude already selected ones
  const filteredLabels = useMemo(() => {
    const available = existingLabels.filter(
      (label) => !selectedLabelUuids.includes(label.uuid)
    );
    
    if (query === '') return available;
    
    return available.filter((label) =>
      label.name?.toLowerCase().includes(query.toLowerCase())
    );
  }, [existingLabels, selectedLabelUuids, query]);

  // Check if the exact typed query already exists to prevent duplicates
  const queryMatchesExisting = existingLabels.some(
    (label) => label.name?.toLowerCase() === query.trim().toLowerCase()
  );

  const handleCreateLabel = () => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery || !projectId || isCreating) return;

    // Check if it's an existing label that's not selected yet
    const existingMatch = existingLabels.find(
      (label) => label.name?.toLowerCase() === trimmedQuery.toLowerCase()
    );

    if (existingMatch) {
      if (!selectedLabelUuids.includes(existingMatch.uuid)) {
        onLabelsChange([...selectedLabelUuids, existingMatch.uuid]);
      }
      setQuery('');
      return;
    }

    createLabel(
      {
        projectUuid: projectId,
        projectLabelName: trimmedQuery,
        projectLabelDescription: trimmedQuery, // Default desc to name for now
      },
      {
        onSuccess: (data) => {
          // Invalidate project labels query to refetch the updated lists!
          queryClient.invalidateQueries({ queryKey: ['project-labels', projectId] });
          
          // Clear query input
          setQuery('');

          // Attempt to optimisticly select the new label if the backend returns it safely
          // Usually we'd do this if the API returns the created object with UUID, 
          // but if it just returns 'status: true', we rely on the refetch. 
        },
      }
    );
  };

  const handleSelect = (selectedLabels: LabelItem[]) => {
    // Headless UI passes the entire array of selected values.
    // If the CREATE_ACTION was just selected, it will be the last item.
    const isCreateAction = selectedLabels.some(l => l.uuid === 'CREATE_ACTION');
    
    if (isCreateAction) {
       handleCreateLabel();
       return; // Do not update the parent's selection array with the pseudo-action
    }

    onLabelsChange(selectedLabels.map(l => l.uuid));
  };

  const isLoading = projectLabels === undefined;

  const selectedObjects = selectedLabelUuids.map(uuidItem => {
     // Robustly extract the string in case uuidItem is actually an object from a dirty payload 
     let uuidStr = typeof uuidItem === 'string' ? uuidItem : String(uuidItem);
     if (typeof uuidItem === 'object' && uuidItem !== null) {
        uuidStr = (uuidItem as any).projectLabelUuid || (uuidItem as any).uuid || String(uuidItem);
     }
     const found = existingLabels.find(l => String(l.uuid) === uuidStr);

     if (found) return { uuid: uuidStr, name: found.name };
     
     // Fallback UI: Hide the raw UUID explicitly so users don't see confusing hashes for stale/orphaned labels
     return { 
       uuid: uuidStr, 
       name: isLoading ? '...' : 'Unknown Label'
     };
  });

  return (
    <div className="w-full relative">
      <Combobox 
        value={selectedObjects} 
        onChange={handleSelect} 
        multiple 
        disabled={disabled || isCreating}
      >
        <div className="relative">
          <div className="flex flex-wrap gap-2 mb-0!">
            {selectedObjects.map((label) => (
              <span
                key={label.uuid}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-white bg-blue-500`}
              >
                {label.name}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    onLabelsChange(selectedLabelUuids.filter((id) => id !== label.uuid));
                  }}
                  className="hover:bg-black/20 rounded-full p-0.5"
                  disabled={disabled}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>

          <div className={`relative w-full cursor-default overflow-hidden rounded-lg bg-primary-variant text-left border border-primary-subtle focus-within:border-primary-light transition-colors sm:text-sm h-10 flex items-center ${className}`}>
            <div className="flex items-center pl-3">
               <Tag className="w-4 h-4 text-neutral-medium shrink-0" />
               <ComboboxInput
                 className="w-full border-none py-2.5 pl-3 pr-10 text-sm leading-5 text-neutral bg-transparent focus:outline-none placeholder-neutral-medium"
                 displayValue={() => ''} // Keep input clear after selection since we render chips outside
                 onChange={(event) => setQuery(event.target.value)}
                 onKeyDown={(e) => {
                   if (e.key === 'Enter' && query.trim()) {
                     e.preventDefault();
                     handleCreateLabel();
                   }
                 }}
                 onFocus={(e) => {
                    // Force open options on focus if not already open
                    if (e.target.value === '') {
                        // This is a trick to trigger the dropdown visibility in some Headless UI versions
                        // but usually ComboboxButton is the cleaner way if wrapped.
                    }
                 }}
                 placeholder="Search or create labels..."
               />
               <ComboboxButton className="absolute inset-y-0 left-0 right-0 w-full h-full flex items-center pr-2 cursor-pointer z-0">
                  {/* Invisible button overlaying the input to handle the "click to open" requirement */}
                  <span className="sr-only">Open labels dropdown</span>
               </ComboboxButton>
               <button 
                 type="button"
                 onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCreateLabel();
                 }}
                 className="absolute inset-y-0 right-0 flex items-center pr-2 z-10 transition-colors"
                 title="Add new label"
               >
                 <Plus className="h-5 w-5 text-neutral-medium hover:text-neutral" aria-hidden="true" />
               </button>
            </div>
          </div>
          
          <ComboboxOptions 
             transition
             className="absolute top-full mt-1 max-h-60 w-full overflow-auto rounded-md bg-primary-variant border-primary-subtle border py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm z-50 "
          >
            {query.trim().length > 0 && !queryMatchesExisting && (
               <ComboboxOption
                 value={{ uuid: 'CREATE_ACTION', name: query }}
                 className="relative cursor-pointer select-none py-2 pl-4 pr-4 transition-colors hover:bg-primary-variant text-neutral"
               >
                 <span className="block truncate font-medium">
                   {isCreating ? 'Creating...' : `Create new label: "${query}"`}
                 </span>
               </ComboboxOption>
            )}

            {filteredLabels.length === 0 && query === '' ? (
              <div className="relative cursor-default select-none py-2 px-4 text-neutral-medium">
                No labels found. Type to create one.
              </div>
            ) : (
              filteredLabels.map((label) => (
                <ComboboxOption
                  key={label.uuid}
                  className={({ active }) =>
                    `relative cursor-pointer select-none py-2 pl-4 pr-4 transition-colors ${
                      active ? 'bg-primary-variant text-neutral' : 'text-neutral-medium'
                    }`
                  }
                  value={label}
                >
                  {({ selected, active }) => (
                    <>
                      <span
                        className={`block truncate ${
                          selected ? 'font-medium text-neutral' : 'font-normal'
                        }`}
                      >
                        {label.name}
                      </span>
                      {selected ? (
                        <span
                          className={`absolute inset-y-0 right-0 flex items-center pr-3 ${
                            active ? 'text-teal-400' : 'text-teal-500'
                          }`}
                        >
                          ✔
                        </span>
                      ) : null}
                    </>
                  )}
                </ComboboxOption>
              ))
            )}
          </ComboboxOptions>
        </div>
      </Combobox>
    </div>
  );
};

export default LabelPicker;
